import React, { useState } from 'react';
import {
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Warning as WarningIcon,
} from '@material-ui/icons';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  FileRow,
  FileThumb,
  FileThumbImage,
  FileBadge,
  FileInfo,
  FileName,
  FileSize,
  RemoveButton,
  EyeOverlay,
  ImagePreviewDialog,
  ImagePreviewImg,
  SizeWarning,
} from './styles';

interface FileCustom extends File {
  preview?: string;
}

interface FileListModalProps {
  files: File[];
  open: boolean;
  onClose: () => void;
  onRemove: (file: File) => void;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getExtension(fileName: string): string {
  const parts = fileName.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : 'FILE';
}

export default function FileListModal({
  files,
  open,
  onClose,
  onRemove,
}: FileListModalProps) {
  const [expandedSrc, setExpandedSrc] = useState<string | null>(null);

  const totalSize = files.reduce((sum, f) => sum + f.size, 0);
  const exceedsLimit = totalSize > 5 * 1024 * 1024;

  return (
    <>
      <Dialog open={open} onClose={onClose} aria-labelledby="file-list-dialog">
        <DialogTitle disableTypography>
          <span className="title">Attachments ({files.length})</span>
        </DialogTitle>
        {exceedsLimit && (
          <SizeWarning>
            <WarningIcon fontSize="small" />
            Total size exceeds 5 MB ({formatSize(totalSize)}). Please remove
            some files.
          </SizeWarning>
        )}
        <DialogContent>
          {files.map(file => {
            const f = file as FileCustom;
            const isImage = f.type.startsWith('image/');
            return (
              <FileRow key={f.name}>
                <FileThumb>
                  {isImage && f.preview ? (
                    <>
                      <FileThumbImage src={f.preview} alt={f.name} />
                      <EyeOverlay
                        aria-label={`Preview ${f.name}`}
                        onClick={() => setExpandedSrc(f.preview!)}>
                        <VisibilityIcon fontSize="small" />
                      </EyeOverlay>
                    </>
                  ) : (
                    <FileBadge>{getExtension(f.name)}</FileBadge>
                  )}
                </FileThumb>
                <FileInfo>
                  <FileName title={f.name}>{f.name}</FileName>
                  <FileSize>{formatSize(f.size)}</FileSize>
                </FileInfo>
                <RemoveButton
                  aria-label={`Remove ${f.name}`}
                  onClick={() => onRemove(file)}>
                  <DeleteIcon fontSize="small" />
                </RemoveButton>
              </FileRow>
            );
          })}
        </DialogContent>
      </Dialog>

      <ImagePreviewDialog
        open={!!expandedSrc}
        onClose={() => setExpandedSrc(null)}
        aria-label="Image preview">
        {expandedSrc && (
          <ImagePreviewImg
            src={expandedSrc}
            alt="Preview"
            onClick={() => setExpandedSrc(null)}
          />
        )}
      </ImagePreviewDialog>
    </>
  );
}
